import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { hrApi } from '@/api';
import { Select } from '@/components/ui/Select';
import { SearchableMultiSelect } from '@/components/ui/SearchableMultiSelect';
import { queryKeys, tierOptions } from '@/lib/query';

type HrAssetTypeSelectProps = {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  required?: boolean;
  activeOnly?: boolean;
};

export function HrAssetTypeSelect({
  value,
  onChange,
  label,
  required,
  activeOnly = true,
}: HrAssetTypeSelectProps) {
  const { t } = useTranslation('hr');
  const { data: types = [] } = useQuery({
    queryKey: queryKeys.hr.assetTypes(),
    queryFn: () => hrApi.listAssetTypes(),
    ...tierOptions('master'),
  });

  const options = useMemo(
    () =>
      types
        .filter((type) => !activeOnly || type.active !== false)
        .map((type) => ({ value: type.code, label: `${type.name} (${type.code})` })),
    [types, activeOnly]
  );

  return (
    <Select
      label={label ?? t('labels.assetType')}
      required={required}
      value={value}
      onChange={onChange}
      options={
        options.length > 0
          ? options
          : [{ value: '', label: t('pages.noAssetTypesYetAddLaptop') }]
      }
    />
  );
}

export function HrAssetTypeMultiSelect({
  values = [],
  onChange,
  label,
  placeholder,
  required,
  activeOnly = true,
}: {
  values?: string[];
  onChange?: (values: string[]) => void;
  label?: string;
  placeholder?: string;
  required?: boolean;
  activeOnly?: boolean;
}) {
  const { t } = useTranslation('hr');
  const { data: types = [], isLoading } = useQuery({
    queryKey: queryKeys.hr.assetTypes(),
    queryFn: () => hrApi.listAssetTypes(),
    ...tierOptions('master'),
  });

  const options = useMemo(
    () =>
      types
        .filter((type) => !activeOnly || type.active !== false)
        .map((type) => ({ value: type.code, label: `${type.name} (${type.code})` })),
    [types, activeOnly]
  );

  return (
    <SearchableMultiSelect
      label={label ?? t('labels.assetType')}
      required={required}
      values={values}
      onChange={onChange}
      options={options}
      loading={isLoading}
      placeholder={placeholder ?? 'Select Onboarding Assets...'}
      emptyMessage="No asset types found in HR Master Data"
    />
  );
}
